/**
 * Wave CM1 — client portal: rings when the firm calls, and runs the call.
 *
 * Polls /api/portal/calls/incoming while the portal tab is visible. When a call
 * rings, the client sees who is calling and — if the firm asked to record —
 * chooses whether to allow recording before answering. Declining recording
 * never blocks the call.
 * Standing React rule: sub-components live at module scope.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { CallEnd, FiberManualRecord, Mic, MicOff, PhoneInTalk, Videocam, VideocamOff } from '@mui/icons-material';
import { toast } from 'react-hot-toast';
import { getPortalToken } from '../../lib/authToken';
import {
  RingTone,
  formatClock,
  getCallMedia,
  mediaErrorMessage,
  stopStream,
  waitForIceGathering,
  webrtcSupported,
} from '../../lib/webrtcCall';

const POLL_MS = 5000;

type Phase = 'idle' | 'ringing' | 'answering' | 'connected' | 'ended';

interface IncomingCall {
  id: string;
  status: string;
  videoEnabled: boolean;
  recordRequested: boolean;
  recordingConsent: 'accepted' | 'declined' | null;
  callerName: string;
  offerSdp?: string;
}

const headers = (): Record<string, string> => ({
  Authorization: `Bearer ${getPortalToken()}`,
  'Content-Type': 'application/json',
});

async function portalApi<T = any>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, { ...init, headers: headers() });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json?.message || 'Request failed');
  return json.data as T;
}

interface RingingViewProps {
  call: IncomingCall;
  busy: boolean;
  onAnswer: (consent: 'accepted' | 'declined' | null) => void;
  onDecline: () => void;
}

const RingingView: React.FC<RingingViewProps> = ({ call, busy, onAnswer, onDecline }) => (
  <>
    <DialogContent>
      <Stack alignItems="center" spacing={1.5} sx={{ py: 1 }}>
        <PhoneInTalk color="primary" sx={{ fontSize: 48 }} />
        <Typography variant="h6" align="center">{call.callerName} is calling</Typography>
        <Typography color="text.secondary" align="center">
          {call.videoEnabled ? 'Video call' : 'Voice call'} from your legal team, through this secure portal.
        </Typography>
        {call.recordRequested && (
          <Alert severity="info" sx={{ width: '100%' }}>
            Your legal team would like to <strong>record this call</strong> for your file. The recording stays private to the
            firm and is protected by attorney–client privilege. You can say no and still take the call.
          </Alert>
        )}
      </Stack>
    </DialogContent>
    <DialogActions sx={{ flexWrap: 'wrap', gap: 1, justifyContent: 'center', pb: 2 }}>
      <Button color="error" variant="outlined" onClick={onDecline} disabled={busy}>Decline</Button>
      {call.recordRequested ? (
        <>
          <Button variant="outlined" onClick={() => onAnswer('declined')} disabled={busy}>Answer — don&apos;t record</Button>
          <Button variant="contained" color="success" onClick={() => onAnswer('accepted')} disabled={busy}>Answer and allow recording</Button>
        </>
      ) : (
        <Button variant="contained" color="success" onClick={() => onAnswer(null)} disabled={busy}>Answer</Button>
      )}
    </DialogActions>
  </>
);

const PortalCallWatcher: React.FC = () => {
  const [phase, setPhase] = useState<Phase>('idle');
  const [call, setCall] = useState<IncomingCall | null>(null);
  const [consent, setConsent] = useState<'accepted' | 'declined' | null>(null);
  const [muted, setMuted] = useState(false);
  const [cameraOff, setCameraOff] = useState(false);
  const [localVideo, setLocalVideo] = useState(false);
  const [remoteHasVideo, setRemoteHasVideo] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [endText, setEndText] = useState('');

  const phaseRef = useRef<Phase>('idle');
  const dismissedRef = useRef<Set<string>>(new Set());
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const localRef = useRef<MediaStream | null>(null);
  const remoteRef = useRef<MediaStream | null>(null);
  const callIdRef = useRef<string | null>(null);
  const connectedAtRef = useRef<number | null>(null);
  const ringRef = useRef<RingTone | null>(null);
  const iceRef = useRef<RTCIceServer[] | null>(null);
  const remoteEl = useRef<HTMLVideoElement | null>(null);
  const localEl = useRef<HTMLVideoElement | null>(null);

  const go = (p: Phase) => {
    phaseRef.current = p;
    setPhase(p);
  };

  const stopRing = () => {
    ringRef.current?.stop();
    ringRef.current = null;
  };

  const teardown = useCallback(() => {
    stopRing();
    pcRef.current?.close();
    pcRef.current = null;
    stopStream(localRef.current);
    localRef.current = null;
    remoteRef.current = null;
  }, []);

  const finish = useCallback((text: string) => {
    if (phaseRef.current === 'idle' || phaseRef.current === 'ended') return;
    teardown();
    setEndText(text);
    go('ended');
  }, [teardown]);

  // Poll for incoming calls while the tab is visible and we are not on a call.
  useEffect(() => {
    let stopped = false;
    const tick = async () => {
      if (stopped || document.visibilityState !== 'visible') return;
      if (phaseRef.current !== 'idle' && phaseRef.current !== 'ringing') return;
      try {
        const data = await portalApi<{ enabled: boolean; call: IncomingCall | null }>('/api/portal/calls/incoming');
        if (stopped) return;
        const incoming = data.call;
        if (incoming && !dismissedRef.current.has(incoming.id) && phaseRef.current === 'idle') {
          callIdRef.current = incoming.id;
          setCall(incoming);
          setConsent(null);
          go('ringing');
          if (!ringRef.current) {
            ringRef.current = new RingTone();
            ringRef.current.start();
          }
        } else if (!incoming && phaseRef.current === 'ringing') {
          // The firm hung up or it timed out.
          stopRing();
          setCall(null);
          go('idle');
          toast('You missed a call from your legal team. Use "Request a call back" to reach them.', { icon: '📞', duration: 8000 });
        }
      } catch {
        /* offline or signed out — try again later */
      }
    };
    tick();
    const timer = setInterval(tick, POLL_MS);
    const onVis = () => { if (document.visibilityState === 'visible') tick(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      stopped = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  // During a call, watch for the firm hanging up.
  useEffect(() => {
    if (phase !== 'connected' && phase !== 'answering') return undefined;
    const t = setInterval(async () => {
      const id = callIdRef.current;
      if (!id) return;
      try {
        const row = await portalApi<IncomingCall>(`/api/portal/calls/${id}`);
        if (!['ringing', 'connected'].includes(row.status)) finish('The call has ended.');
      } catch {
        /* transient */
      }
    }, 4000);
    return () => clearInterval(t);
  }, [phase, finish]);

  useEffect(() => {
    if (phase !== 'connected') return undefined;
    const t = setInterval(() => {
      if (connectedAtRef.current) setElapsed((Date.now() - connectedAtRef.current) / 1000);
    }, 1000);
    return () => clearInterval(t);
  }, [phase]);

  useEffect(() => {
    if (remoteEl.current && remoteRef.current && remoteEl.current.srcObject !== remoteRef.current) {
      remoteEl.current.srcObject = remoteRef.current;
      remoteEl.current.play().catch(() => undefined);
    }
    if (localEl.current && localRef.current && localEl.current.srcObject !== localRef.current) {
      localEl.current.srcObject = localRef.current;
    }
  });

  // Closing the portal tab mid-call hangs up.
  useEffect(() => {
    const onHide = () => {
      const id = callIdRef.current;
      if (id && (phaseRef.current === 'connected' || phaseRef.current === 'answering')) {
        fetch(`/api/portal/calls/${id}/end`, { method: 'POST', headers: headers(), keepalive: true, body: '{}' }).catch(() => undefined);
      }
    };
    window.addEventListener('pagehide', onHide);
    return () => {
      window.removeEventListener('pagehide', onHide);
      onHide();
      teardown();
    };
  }, [teardown]);

  const decline = async () => {
    const id = callIdRef.current;
    stopRing();
    if (id) {
      dismissedRef.current.add(id);
      await portalApi(`/api/portal/calls/${id}/decline`, { method: 'POST', body: '{}' }).catch(() => undefined);
    }
    setCall(null);
    go('idle');
  };

  const answer = async (choice: 'accepted' | 'declined' | null) => {
    if (!call?.offerSdp) return;
    if (!webrtcSupported()) {
      toast.error('This browser cannot take calls. Please use a current Chrome, Edge, Firefox or Safari.');
      return;
    }
    stopRing();
    go('answering');
    setConsent(choice);
    try {
      if (!iceRef.current) {
        const cfg = await portalApi<{ iceServers: RTCIceServer[] }>('/api/portal/calls/config');
        iceRef.current = cfg.iceServers || [];
      }
      let media;
      try {
        media = await getCallMedia(call.videoEnabled);
      } catch (err) {
        throw new Error(mediaErrorMessage(err));
      }
      localRef.current = media.stream;
      setLocalVideo(media.video);

      const pc = new RTCPeerConnection({ iceServers: iceRef.current });
      pcRef.current = pc;
      remoteRef.current = new MediaStream();
      pc.ontrack = (e) => {
        remoteRef.current?.addTrack(e.track);
        if (e.track.kind === 'video') setRemoteHasVideo(true);
        if (remoteEl.current && remoteEl.current.srcObject !== remoteRef.current) {
          remoteEl.current.srcObject = remoteRef.current;
        }
        remoteEl.current?.play().catch(() => undefined);
      };
      pc.onconnectionstatechange = () => {
        if (pc.connectionState === 'connected' && phaseRef.current === 'answering') {
          connectedAtRef.current = Date.now();
          go('connected');
        }
        if (pc.connectionState === 'failed') {
          const id = callIdRef.current;
          if (id) portalApi(`/api/portal/calls/${id}/end`, { method: 'POST', body: '{}' }).catch(() => undefined);
          finish('The connection dropped. Your legal team can call you again, or use "Request a call back".');
        }
      };

      await pc.setRemoteDescription({ type: 'offer', sdp: call.offerSdp });
      media.stream.getTracks().forEach((t) => pc.addTrack(t, media.stream));
      const ans = await pc.createAnswer();
      await pc.setLocalDescription(ans);
      await waitForIceGathering(pc);
      const sdp = pc.localDescription?.sdp;
      if (!sdp) throw new Error('Could not answer the call.');
      await portalApi(`/api/portal/calls/${call.id}/answer`, {
        method: 'POST',
        body: JSON.stringify({ answerSdp: sdp, recordingConsent: call.recordRequested ? choice : undefined }),
      });
    } catch (err) {
      const id = callIdRef.current;
      if (id) {
        dismissedRef.current.add(id);
        portalApi(`/api/portal/calls/${id}/decline`, { method: 'POST', body: '{}' }).catch(() => undefined);
      }
      teardown();
      setCall(null);
      go('idle');
      toast.error((err as Error)?.message || 'Could not answer the call.');
    }
  };

  const hangUp = async () => {
    const id = callIdRef.current;
    if (id) await portalApi(`/api/portal/calls/${id}/end`, { method: 'POST', body: '{}' }).catch(() => undefined);
    finish('You ended the call.');
  };

  const closeEnded = () => {
    if (callIdRef.current) dismissedRef.current.add(callIdRef.current);
    callIdRef.current = null;
    connectedAtRef.current = null;
    setCall(null);
    setElapsed(0);
    setMuted(false);
    setCameraOff(false);
    setRemoteHasVideo(false);
    setLocalVideo(false);
    go('idle');
  };

  const toggleMute = () => {
    const next = !muted;
    localRef.current?.getAudioTracks().forEach((t) => { t.enabled = !next; });
    setMuted(next);
  };

  const toggleCamera = () => {
    const next = !cameraOff;
    localRef.current?.getVideoTracks().forEach((t) => { t.enabled = !next; });
    setCameraOff(next);
  };

  if (phase === 'idle' || !call) return null;
  const videoCall = call.videoEnabled && (remoteHasVideo || localVideo);
  const recording = call.recordRequested && consent === 'accepted' && phase === 'connected';

  return (
    <Dialog open fullWidth maxWidth={videoCall && phase !== 'ringing' ? 'md' : 'xs'} disableEscapeKeyDown>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {phase === 'ringing' ? 'Incoming call' : call.callerName}
        <Box sx={{ flex: 1 }} />
        {recording && <Chip size="small" color="error" icon={<FiberManualRecord />} label="Recording" />}
        {phase === 'connected' && <Chip size="small" label={formatClock(elapsed)} />}
      </DialogTitle>

      {phase === 'ringing' && <RingingView call={call} busy={false} onAnswer={answer} onDecline={decline} />}

      {(phase === 'answering' || phase === 'connected') && (
        <>
          <DialogContent>
            <Box sx={{ position: 'relative', bgcolor: videoCall ? 'common.black' : 'transparent', borderRadius: 1, overflow: 'hidden' }}>
              <video ref={remoteEl} autoPlay playsInline style={{ width: '100%', maxHeight: 480, display: videoCall && remoteHasVideo ? 'block' : 'none' }} />
              {localVideo && (
                <video
                  ref={localEl}
                  autoPlay
                  playsInline
                  muted
                  style={{
                    position: remoteHasVideo ? 'absolute' : 'static',
                    right: 12,
                    bottom: 12,
                    width: remoteHasVideo ? 140 : '100%',
                    borderRadius: 6,
                    border: '2px solid rgba(255,255,255,0.6)',
                    transform: 'scaleX(-1)',
                  }}
                />
              )}
            </Box>
            <Typography align="center" sx={{ mt: 2 }}>
              {phase === 'answering' ? 'Connecting…' : videoCall ? '' : `On call with ${call.callerName}`}
            </Typography>
            {call.recordRequested && consent === 'declined' && (
              <Typography variant="body2" color="text.secondary" align="center">This call is not being recorded.</Typography>
            )}
            <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 2 }}>
              <Tooltip title={muted ? 'Unmute' : 'Mute'}>
                <IconButton onClick={toggleMute} color={muted ? 'warning' : 'default'} sx={{ border: 1, borderColor: 'divider' }} aria-label={muted ? 'Unmute' : 'Mute'}>
                  {muted ? <MicOff /> : <Mic />}
                </IconButton>
              </Tooltip>
              {localVideo && (
                <Tooltip title={cameraOff ? 'Turn camera on' : 'Turn camera off'}>
                  <IconButton onClick={toggleCamera} color={cameraOff ? 'warning' : 'default'} sx={{ border: 1, borderColor: 'divider' }} aria-label="Camera">
                    {cameraOff ? <VideocamOff /> : <Videocam />}
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Hang up">
                <IconButton onClick={hangUp} sx={{ bgcolor: 'error.main', color: 'common.white', '&:hover': { bgcolor: 'error.dark' } }} aria-label="Hang up">
                  <CallEnd />
                </IconButton>
              </Tooltip>
            </Stack>
          </DialogContent>
        </>
      )}

      {phase === 'ended' && (
        <>
          <DialogContent>
            <Alert severity="info">{endText}</Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeEnded}>Close</Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default PortalCallWatcher;
